/**
 * What a report needs before it can be worked, per kind — the client's copy of the platform's
 * completeness rubric (bata-platform ADR 2137 decision 5, ADR 1020; bata-ui ADR 9071).
 *
 * The platform's rubric is the authority: it answers 422 on a report it finds incomplete, and it
 * is what decides whether OPTIMA's draft can be confirmed. This copy only lets the form mark the
 * missing fields before «Send», so the person is not refused after typing. Pure, so the test
 * asserts on the table.
 *
 * The field codes are the platform's (`examples`, `acceptance_criteria` …): the OPTIMA panel shows
 * the platform's `missing` list with the same words, through {@link FIELD_LABEL_KEY}.
 */

import type { FeedbackKind } from './api';
import { FEEDBACK_LIMITS } from './api';
import { GENERAL_SECTION, NEW_AREA_SECTION } from './sections';

/** The rubric's fields, in the order the form draws them. */
export const RUBRIC_FIELDS = [
  'title', 'requirement', 'examples', 'screens', 'markets', 'runs', 'expected_figures', 'acceptance_criteria', 'steps',
] as const;
export type RubricField = (typeof RUBRIC_FIELDS)[number];

/** The words for each field code; a code this build does not know is shown as itself. */
export const FIELD_LABEL_KEY: Readonly<Record<RubricField, string>> = {
  title: 'feedback.field.title',
  requirement: 'feedback.field.requirement',
  examples: 'feedback.field.examples',
  screens: 'feedback.field.screens',
  markets: 'feedback.field.markets',
  runs: 'feedback.field.runs',
  expected_figures: 'feedback.field.expectedFigures',
  acceptance_criteria: 'feedback.field.acceptanceCriteria',
  steps: 'feedback.field.steps',
};

export function fieldWords(t: (key: string) => string, code: string): string {
  return Object.hasOwn(FIELD_LABEL_KEY, code) ? t(FIELD_LABEL_KEY[code as RubricField]) : code;
}

/** Which fields each kind asks for at all: a field a kind does not ask for is not drawn on the form. */
export const FIELDS_OF_KIND: Readonly<Record<FeedbackKind, ReadonlySet<RubricField>>> = {
  clarification: new Set<RubricField>(['title', 'requirement', 'screens']),
  data: new Set<RubricField>(['title', 'requirement', 'examples', 'screens', 'markets', 'runs', 'expected_figures', 'steps']),
  incident: new Set<RubricField>(['title', 'requirement', 'examples', 'screens', 'markets', 'runs', 'expected_figures', 'acceptance_criteria', 'steps']),
  enhancement: new Set<RubricField>(['title', 'requirement', 'examples', 'screens', 'markets', 'expected_figures', 'acceptance_criteria']),
};

/** Which of those are required. `steps` is never; `screens` is optional for a clarification. */
const REQUIRED_OF_KIND: Readonly<Record<FeedbackKind, ReadonlySet<RubricField>>> = {
  clarification: new Set<RubricField>(['title', 'requirement']),
  data: new Set<RubricField>(['title', 'requirement', 'examples', 'screens', 'markets', 'runs', 'expected_figures']),
  incident: new Set<RubricField>(['title', 'requirement', 'examples', 'screens', 'markets', 'runs', 'expected_figures', 'acceptance_criteria']),
  enhancement: new Set<RubricField>(['title', 'requirement', 'examples', 'screens', 'markets', 'expected_figures', 'acceptance_criteria']),
};

export interface RubricInput {
  readonly kind: FeedbackKind;
  readonly title: string;
  readonly requirement: string;
  readonly examples: string;
  /** The section ids chosen; «General» says no screen. */
  readonly screens: readonly string[];
  readonly markets: readonly string[];
  readonly runs: readonly string[];
  readonly expectedFigures: string;
  /** An enhancement may say why no figure applies instead of giving one. */
  readonly expectedFiguresNotApplicableReason: string;
  readonly acceptanceCriteria: readonly string[];
}

const given = (text: string): boolean => text.trim() !== '';
const anyGiven = (items: readonly string[]): boolean => items.some(given);

/** Whether a field holds what the rubric asks of it. */
function satisfied(field: RubricField, input: RubricInput): boolean {
  switch (field) {
    case 'title': return input.title.trim().length >= FEEDBACK_LIMITS.title.min;
    case 'requirement': return input.requirement.trim().length >= FEEDBACK_LIMITS.description.min;
    case 'examples': return given(input.examples);
    // «New area» names the screen an enhancement asks for; «General» names none.
    case 'screens': return input.screens.some((id) => id !== GENERAL_SECTION && (id !== NEW_AREA_SECTION || input.kind === 'enhancement'));
    case 'markets': return anyGiven(input.markets);
    case 'runs': return anyGiven(input.runs);
    case 'expected_figures':
      return given(input.expectedFigures) || (input.kind === 'enhancement' && given(input.expectedFiguresNotApplicableReason));
    case 'acceptance_criteria': return anyGiven(input.acceptanceCriteria);
    case 'steps': return true;
    default: return true;
  }
}

/** The required fields still missing, in the form's order. Empty: the report can be sent. */
export function missingFields(input: RubricInput): readonly RubricField[] {
  const required = REQUIRED_OF_KIND[input.kind];
  return RUBRIC_FIELDS.filter((field) => required.has(field) && !satisfied(field, input));
}

export function isRequired(kind: FeedbackKind, field: RubricField): boolean {
  return REQUIRED_OF_KIND[kind].has(field);
}
